/**
 * **The rave view.** One component, every page, both layouts.
 *
 * Filters, the card ⇄ table toggle, the sort, the empty states and the two
 * layouts themselves — all of it, once. A page hands over an array of raves
 * and gets the whole thing; it does not lay out a grid, decide what "upcoming"
 * means, or remember which view somebody chose.
 *
 * That is the point. My raves and All raves each wired this up separately and
 * the two had already drifted — different grids, different empty copy, and a
 * card that rendered one way on one page and another way on the other. One
 * object shown two ways in one product is how people stop recognising it.
 *
 * **Anything that shows a list of raves calls this.** If a screen needs
 * something this does not do, the option belongs here rather than in a second
 * copy of the layout.
 */
import { createContext, useContext, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Button, Empty, Row, Stack, Txt, Wrap } from '@/ui';
import { useT } from '@/i18n';
import { RaveCard, RaveGrid, timingOf, type Rave } from './RaveCard';
import { CountdownPanel } from '../Countdown';
import { RaveTable } from './RaveTable';
import { RaveFilters, ViewToggle, applyFilter, useRaveFilter, useRaveView } from './RaveFilters';
import { useIncremental } from './useIncremental';
import { LogoArt } from '../Logo';
import './RaveList.css';

export function RaveList({
  raves, now = Date.now(), onAttend, busyId, withClashes, empty, footer, onOpen, openLabel, admin,
}: {
  raves: Rave[];
  /** Read once by the caller so every row agrees on "now". */
  now?: number;
  onAttend?: (editionId: string, subdomain: string, editionSlug?: string | null) => void;
  busyId?: string | null;
  /** Offer the clashes filter — only where the raves carry counters. */
  withClashes?: boolean;
  /** An admin's list: opens on "All", past editions included. */
  admin?: boolean;
  /** What to show when there are no raves AT ALL, as against none matching. */
  empty?: ReactNode;
  /** Anything the page wants under the list, e.g. "Find another rave". */
  footer?: ReactNode;
  /**
   * Open a rave HERE rather than going to its own app.
   *
   * The rave admin's dashboard is this same list — the user has said three
   * times that My raves, Find a rave and this are one view — but its cards
   * lead to a settings page on `radm.`, not to the festival's application.
   */
  onOpen?: (editionId: string) => void;
  /** What the card's action says when `onOpen` is given. */
  openLabel?: string;
}) {
  /*
   * One countdown panel for the whole list, opened by whichever card asked.
   *
   * Not one per card: a grid of a dozen would mount a dozen ticking clocks to
   * show at most one of them.
   */
  const tr = useT();
  const [countdown, setCountdown] = useState<Rave | null>(null);
  const [view, setView] = useRaveView();
  // Past raves hidden until asked for — see `RaveFilters`.
  const [filter, setFilter] = useRaveFilter(withClashes, admin);

  /*
   * "Nothing here" and "nothing matches" are different facts and get different
   * screens. The first is the PAGE's to phrase — it knows whether this is a
   * catalogue with nothing published or an account attending nothing. The
   * second is this component's, because this component owns the filter.
   */
  const shown = applyFilter(raves, filter, now);
  // A few at a time, more as the reader scrolls — see `useIncremental`. The
  // filters above still count EVERY rave.
  const page = useIncremental(shown, `${filter}|${view}`);
  /* On a `RavePage` the card/list toggle sits beside the title instead. */
  const titleTools = useContext(TitleTools);

  if (raves.length === 0) return <>{empty ?? <Empty title={tr('No raves yet')} />}</>;
  return (
    <Stack gap={16}>
      <RaveFilters
        raves={raves}
        now={now}
        filter={filter}
        onFilter={setFilter}
        view={titleTools ? undefined : view}
        onView={titleTools ? undefined : setView}
        withClashes={withClashes}
      />
      {titleTools && createPortal(<ViewToggle view={view} onChange={setView} />, titleTools)}

      {shown.length === 0 && filter === 'upcoming' && raves.some((r) => timingOf(r, now).tone === 'past') ? (
        /*
         * Nothing coming up, but raves that happened. The default list is
         * empty BECAUSE past ones are hidden, and saying only "nothing
         * matches" would read as "you have no raves" to somebody who has
         * five from last summer.
         */
        <Empty
          title={tr('No upcoming raves')}
          hint={tr('Past raves are hidden until you ask for them.')}
          action={<Button variant="secondary" onClick={() => setFilter('past')}>{tr('Show past raves')}</Button>}
        />
      ) : shown.length === 0 ? (
        <Empty
          title={tr('Nothing matches that filter')}
          hint={tr('Try another one, or show them all.')}
          action={<Button variant="secondary" onClick={() => setFilter('all')}>{tr('Show all')}</Button>}
        />
      ) : view === 'card' ? (
        <RaveGrid>
          {page.visible.map((r) => (
            <RaveCard
              key={r.editionId}
              rave={r}
              now={now}
              busy={busyId === r.editionId}
              onAttend={onAttend ? () => onAttend(r.editionId, r.subdomain, r.editionSlug) : undefined}
              onCountdown={setCountdown}
              onOpen={onOpen ? () => onOpen(r.editionId) : undefined}
              openLabel={openLabel}
            />
          ))}
        </RaveGrid>
      ) : (
        <RaveTable raves={page.visible} now={now} onAttend={onAttend} busyId={busyId} />
      )}

      {page.hasMore && (
        <div ref={page.sentinel} className="rave-list-more">
          <Button variant="secondary" onClick={page.more}>
            {tr('Show more raves ({count} left)', { count: page.remaining })}
          </Button>
        </div>
      )}

      {countdown && (
        <CountdownPanel
          onClose={() => setCountdown(null)}
          name={countdown.editionName}
          venue={[countdown.venue, countdown.country].filter(Boolean).join(' · ') || undefined}
          gatesOpenAt={countdown.gatesOpenAt ?? undefined}
          endsAt={countdown.endsAt ?? undefined}
          coverUrl={countdown.coverUrl ?? undefined}
          /* The rave's own mark where it has one. `Logo` reads the edition
             bundle and there is none out here, so the card's URL is used
             directly. */
          logo={countdown.logoUrl
            ? <LogoArt src={countdown.logoUrl} alt={countdown.festivalName} height={46} className="rave-list-countdown-logo" />
            : <Txt size={26} weight={800}>{countdown.festivalName}</Txt>}
          timezone={countdown.timezone ?? 'UTC'}
        />
      )}

      {footer}
    </Stack>
  );
}

/**
 * The page a list of raves sits on — the measure, the padding, the title.
 *
 * My raves and Find a rave had two of these and they did not match: the
 * directory capped its content at 560px while the dashboard used the app's
 * measure of 980, so the same cards were two-up on one page and three-up on
 * the other. Same component, same object, different size — which is exactly
 * the drift `RaveList` exists to stop, one level up.
 */
export function RavePage({ title, children }: { title: string; children: ReactNode }) {
  /* A state, not a ref: the list below must re-render once the node exists. */
  const [tools, setTools] = useState<HTMLElement | null>(null);
  return (
    <Wrap className="rave-list-page">
      <Stack gap={16}>
        {/*
          * The card/list toggle is on the TITLE's row (the user, 2026-10-07),
          * so the filter row below is all chips — on a phone that is two more
          * chips in view before it has to scroll.
          */}
        <Row gap={12} className="rave-list-head">
          <Txt as="h1" weight={800} track={-0.025} className="rave-list-title">{title}</Txt>
          <span ref={setTools} className="rave-list-tools" />
        </Row>
        <TitleTools.Provider value={tools}>{children}</TitleTools.Provider>
      </Stack>
    </Wrap>
  );
}

/** Where a `RaveList` inside a `RavePage` puts its view toggle. */
const TitleTools = createContext<HTMLElement | null>(null);
